import React from 'react';
import PhotosEmptyState from 'images/PhotosEmptyState.png';
import EmptyStateContainer from 'components/shared/DesignSystem/shared/components/EmptyStateContainer';
import { tokens } from '@companycam/slab-tokens';
import { Billboard } from '@companycam/slab-web';
import { translate } from 'javascripts/translations/i18n';
import styled, { keyframes } from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LD_NEW_EMPTY_STATES } from 'constants/featureFlags';
import { useCurrentUser } from 'javascripts/hooks';
import { ADMIN, hasRoleAccess, MANAGER } from 'helpers/roles';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';

type ProjectEmptyAssetFeedProps = {
  setUploadModalVisible: (visible: boolean) => void;
  openAssignUsersModal: () => void;
};

const billboardEnter = keyframes`
  to {
    opacity: 1;
  }
`;

const StyledBillboard = styled(Billboard)`
  opacity: 0;
  animation-name: ${billboardEnter};
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  border: var(--cc_size_border_width_m) solid
    var(--cc_color_button_background_subtle);
  border-radius: var(--cc_size_border_radius_l);
  padding: 10%;
  width: 100%;
  p {
    margin-top: var(--cc_size_spacing_s);
  }
  button {
    margin-top: var(--cc_size_spacing_m);
  }
`;

function ProjectEmptyAssetFeed({
  setUploadModalVisible,
  openAssignUsersModal,
}: ProjectEmptyAssetFeedProps) {
  const flags = useFlags();
  const shouldShowNewEmptyState = flags[LD_NEW_EMPTY_STATES];
  const user = useCurrentUser();
  const canAssignUsers = hasRoleAccess(user, [ADMIN, MANAGER]);

  const uploadPhotos = () => {
    Steven.elementClicked({
      id: 'project_photos_empty_state_upload_photos_button',
      content: 'assetFeed.filterRow.uploadPhotos',
      type: ELEMENT_TYPES.BUTTON,
      action: 'project_photos_empty_state_upload_photos_button_clicked',
    });
    setUploadModalVisible(true);
  };

  const assignUsers = () => {
    Steven.elementClicked({
      id: 'project_photos_empty_state_assign_users_button',
      content: 'assetFeed.emptyState.secondaryCta',
      type: ELEMENT_TYPES.BUTTON,
      action: 'project_photos_empty_state_assign_users_button_clicked',
    });
    openAssignUsersModal();
  };

  return shouldShowNewEmptyState ? (
    <EmptyStateContainer>
      <StyledBillboard
        title={translate('assetFeed.emptyState.emptyTitle')}
        message={translate('assetFeed.emptyState.emptyMessage')}
        primaryAction={{
          'data-testid': 'photos__add-new-photo',
          onClick: uploadPhotos,
          label: translate('assetFeed.filterRow.uploadPhotos'),
          icon: { name: 'plus-circle-outline', position: 'before' },
        }}
        secondaryAction={
          canAssignUsers
            ? {
                'data-testid': 'photos__assign-users',
                onClick: assignUsers,
                label: translate('assetFeed.emptyState.secondaryCta'),
                icon: { name: 'account-multiple-plus', position: 'before' },
              }
            : null
        }
      />
    </EmptyStateContainer>
  ) : (
    <EmptyStateContainer>
      <Billboard
        title={translate('assetFeed.emptyTitle')}
        message={translate('assetFeed.emptyMessage')}
        image={{
          src: PhotosEmptyState,
          alt: translate('assetFeed.altText'),
          imageContainerStyle: {
            width: 350,
            height: 198,
            marginBottom: tokens.size_spacing_l,
            marginTop: tokens.size_spacing_xl,
          },
        }}
        primaryAction={{
          'data-testid': 'photos__add-new-photo',
          onClick: () => setUploadModalVisible(true),
          label: translate('assetFeed.filterRow.uploadPhotos'),
          icon: { name: 'plus-circle-outline', position: 'before' },
        }}
      />
    </EmptyStateContainer>
  );
}

export default ProjectEmptyAssetFeed;
